import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Empty, Spin } from '~shared/ui';
import { ILang } from '~shared/lib/i18n';
import { timezoneDayjs } from '~shared/lib/utils';

import type { ApiParentVoucherNotificationItem } from '../api';

interface ParentVoucherNotificationsListViewProps {
  data: ApiParentVoucherNotificationItem[];
  loading?: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

// Цвета точки по типу события:
// красный — отказ/аннулирование/риск (id_status 5/6/7 или risk_stage>0),
// оранжевый — риск 1 стадии (только предупреждение),
// зелёный — активация (2),
// синий по умолчанию (одобрение комиссии, подписание отказа и пр.).
const pickDotColor = (item: { id_status: number; risk_stage: number | null }): string => {
  if (item.risk_stage === 3) {
    return '#ff4d4f';
  }

  if (item.risk_stage === 2) {
    return '#ff4d4f';
  }

  if (item.risk_stage === 1) {
    return '#FF9500';
  }

  if ([5, 6, 7].includes(item.id_status)) {
    return '#ff4d4f';
  }

  if (item.id_status === 2) {
    return '#0ACD9A';
  }

  return '#007AFF';
};

export const ParentVoucherNotificationsListView: FC<ParentVoucherNotificationsListViewProps> = ({
  data,
  loading,
  hasMore,
  onLoadMore,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as ILang;

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMore) {
      return;
    }

    const node = sentinelRef.current;

    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          onLoadMore();
        }
      },
      { rootMargin: '200px' }
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  if (loading && !data?.length) {
    return (
      <div className="flex justify-center py-10">
        <Spin />
      </div>
    );
  }

  if (!data?.length) {
    return <Empty />;
  }

  return (
    <div className="flex flex-col gap-3">
      {data.map((item) => {
        const title =
          lang === 'ky' ? item.title_ky || item.title_ru : item.title_ru || item.title_ky;

        const description =
          lang === 'ky'
            ? item.description_ky || item.description_ru
            : item.description_ru || item.description_ky;

        const body = lang === 'ky' ? item.body_ky || item.body_ru : item.body_ru || item.body_ky;
        const dotColor = pickDotColor(item);

        const organizationName =
          lang === 'ky'
            ? item.organization_name_ky || item.organization_name_ru
            : item.organization_name_ru || item.organization_name_ky;

        const aymakName =
          lang === 'ky'
            ? item.aymak_name_ky || item.aymak_name_ru
            : item.aymak_name_ru || item.aymak_name_ky;

        const details: { label: string; value: string }[] = [];

        if (item.child_fio) {
          details.push({
            label: t('parent:notificationHistory.child', { defaultValue: 'Ребёнок' }),
            value: item.child_fio,
          });
        }

        if (organizationName) {
          details.push({
            label: t('parent:notificationHistory.kindergarten', { defaultValue: 'Детский сад' }),
            value: organizationName,
          });
        }

        if (aymakName) {
          details.push({
            label: t('parent:notificationHistory.aymak', { defaultValue: 'Айыл окмоту' }),
            value: aymakName,
          });
        }

        return (
          <div
            key={item.id}
            className={`relative rounded-lg border p-4 flex gap-3 ${
              item.unread
                ? 'bg-[rgba(10,205,154,0.06)] border-primary/30'
                : 'bg-white border-stroke'
            }`}
          >
            <span
              className="mt-2 shrink-0 rounded-full"
              style={{ width: 10, height: 10, background: dotColor }}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div className="text-[15px] font-semibold text-black break-words">
                  {title || '—'}
                </div>
                <div className="text-[12px] text-placeholder whitespace-nowrap">
                  {timezoneDayjs(item.created_at).format('DD.MM.YYYY HH:mm')}
                </div>
              </div>
              {description && (
                <div className="mt-1 text-[14px] text-black/80 whitespace-pre-wrap break-words">
                  {description}
                </div>
              )}
              {details.length > 0 && (
                <div className="mt-2 grid grid-cols-1 gap-0.5 text-[12px]">
                  {details.map((d) => (
                    <div key={d.label} className="flex flex-wrap gap-1.5 break-words">
                      <span className="text-placeholder">{d.label}:</span>
                      <span className="text-black/80">{d.value}</span>
                    </div>
                  ))}
                </div>
              )}
              {body && body !== description && (
                <div className="mt-2 text-[12px] text-placeholder whitespace-pre-wrap break-words italic">
                  {body}
                </div>
              )}
            </div>
            {item.unread && (
              <span
                className="absolute top-3 right-3 rounded-full bg-primary"
                style={{ width: 8, height: 8 }}
              />
            )}
          </div>
        );
      })}
      {hasMore && (
        <div ref={sentinelRef} className="flex justify-center py-4">
          {loading && <Spin />}
        </div>
      )}
    </div>
  );
};
